/* eslint-disable @typescript-eslint/no-use-before-define */
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import { TaskCardTitle } from '@euler/app/flows/task/pages/detail/components/TaskCardTitle';
import { StatusColors } from '@euler/components/Colors';
import { Icon, IconType } from '@euler/components/Icomoon';
import { TableView } from '@euler/components/TableView';
import { isFaultLikeServiceSubject } from '@euler/functions/isFaultLikeServiceSubject';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  FaultInspectionItem,
  FaultInspectionItemRelatedJob,
} from '@euler/model/FaultInspectionItem';
import {
  CommonTaskStatus,
  DefectiveLevel,
  InspectionTaskJobStatus,
} from '@euler/model/enum';
import { uniq } from '@euler/utils/array';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { ReactNode, memo, useCallback, useMemo } from 'react';
import { StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';
import { TaskDetailSection } from './TaskDetailSection';

export const WiredFaultInspectionInfo = memo(() => {
  const { detail } = useTaskContext();
  const { rpx } = useResponsiveContext();
  const {
    basicInfo: { orderDetail },
    construction,
  } = detail;

  const items = useMemo<FaultInspectionItem[]>(() => {
    const faultLikeSubjects =
      orderDetail?.serviceSubjects?.filter(isFaultLikeServiceSubject) ?? [];
    return faultLikeSubjects.map<FaultInspectionItem>(subject => {
      const relatedJobs =
        subject.relatedJobs?.map<FaultInspectionItemRelatedJob>(relatedJob => ({
          ...relatedJob,
          jobStatus: InspectionTaskJobStatus.Finished,
        })) ?? [];
      const possibleJobs = construction.jobs.filter(
        x => x.context?.resolved?.includes(subject.name),
      );
      for (const possibleJob of possibleJobs) {
        if (possibleJob.status === InspectionTaskJobStatus.InProgress) {
          const index = relatedJobs.findIndex(
            j => j.jobName === possibleJob.name,
          );
          if (index >= 0) {
            relatedJobs[index].jobStatus = possibleJob.status;
          } else {
            relatedJobs.push({
              jobName: possibleJob.name,
              author: possibleJob.technicianName ?? '',
              timestamp: possibleJob.updatedAt ?? possibleJob.createdAt,
              jobStatus: possibleJob.status,
            });
          }
        }
      }
      return {
        type: subject.type,
        title: subject.name,
        relatedInspections: subject.relatedInspections,
        relatedJobs,
        remark: subject.remark,
        unsolvedReason: subject.unsolvedReason,
        note: subject.note,
        assignedTo: subject.assignedTo,
        status:
          subject.relatedJobs?.length &&
          construction.status === CommonTaskStatus.Finished
            ? 'solved'
            : subject.status,
      };
    });
  }, [construction.jobs, construction.status, orderDetail?.serviceSubjects]);

  const viewReport = useViewReportWithTaskContext();

  const onViewItemInspectionDetails = usePersistFn(
    async (item: FaultInspectionItem) => {
      viewReport('inspectionReport', {
        title: `${item.title}-检测结果`,
        faultLikeSubject: item,
        canFinish: () => false,
        canShare: () => false,
        canEdit: () => false,
      });
    },
  );

  const onViewItemConstructionDetails = usePersistFn(
    async (item: FaultInspectionItem) => {
      viewReport('constructionReport', {
        title: `${item.title}-施工反馈`,
        faultLikeSubject: item,
        canFinish: () => false,
        canShare: () => false,
        canEdit: () => false,
      });
    },
  );

  if (!items.length) {
    return null;
  }

  return (
    <>
      <TaskDetailSection
        title={
          <TaskCardTitle text="故障检修" status={CommonTaskStatus.Pending} />
        }
        headerRight={
          <Typography.Text light size={rpx(12)} color="#777">
            {items.filter(x => x.status === 'solved').length} / {items.length}{' '}
            已完成
          </Typography.Text>
        }
        bodyStyle={{
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <FaultInspectionInfoList
          items={items}
          onViewInspectionDetails={onViewItemInspectionDetails}
          onViewJobDetails={onViewItemConstructionDetails}
        />
      </TaskDetailSection>
    </>
  );
});

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        subTitle: {
          fontSize: rpx(10),
          borderWidth: 0.5,
          borderColor: '#ddd',
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: rpx(3),
          paddingRight: rpx(3),
          lineHeight: rpx(14),
          borderRadius: rpx(3),
          top: rpx(-2),
          color: '#777',
        },
      }),
    [rpx],
  );
};

export const FaultInspectionInfoList = memo(
  ({
    items,
    scene,
    onViewInspectionDetails,
    onViewJobDetails,
  }: {
    items: FaultInspectionItem[];
    scene?: 'task' | 'service-record';
    onViewInspectionDetails?: (item: FaultInspectionItem) => void;
    onViewJobDetails?: (item: FaultInspectionItem) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();

    return (
      <TableView>
        {items.map(item => {
          return (
            <TableView.Item
              key={item.type + ':' + item.title}
              title={
                <StackLayout
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <Typography.Text size={rpx(16)} mr={rpx(8)}>
                    {item.title}
                  </Typography.Text>
                  <FaultInspectionItemStatusBadge item={item} scene={scene} />
                </StackLayout>
              }
              icon={
                <Icon name="warning" size={rpx(32)} color={StatusColors.Warn} />
              }
              titleStyle={{ marginBottom: rpx(5) }}
              titleTextStyle={{ fontSize: rpx(16) }}
              subTitleTextStyle={styles.subTitle}
              note={
                <FaultInspectionItemNote
                  item={item}
                  scene={scene}
                  onViewInspectionDetails={onViewInspectionDetails}
                  onViewJobDetails={onViewJobDetails}
                />
              }
              noteStyle={{ fontSize: rpx(12) }}
              iconStyle={{ marginRight: rpx(10) }}
              css={`
                padding: ${rpx(15)}px 0;
                min-height: ${rpx(74)}px;
              `}
            />
          );
        })}
      </TableView>
    );
  },
);

export const FaultInspectionItemStatusBadge = memo(
  ({
    item,
    scene,
    style,
  }: {
    item: FaultInspectionItem;
    scene?: 'task' | 'service-record';
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();

    const iconName: IconType =
      item.status === 'solved'
        ? 'check'
        : item.status === 'in-progress'
        ? 'tools'
        : 'clock';

    const iconSize = rpx(item.status === 'solved' ? 14 : 12);

    const statusColor =
      item.status === 'solved'
        ? StatusColors.Success
        : item.status === 'in-progress'
        ? '#4f01bb'
        : '#fbb03b';

    const statusLabel =
      item.status === 'solved'
        ? '检修完成'
        : scene === 'service-record'
        ? '遗留'
        : item.status === 'in-progress'
        ? '检修中'
        : '待检修';

    return (
      <StackLayout
        direction="row"
        alignItems="center"
        spacing={rpx(2)}
        style={[
          {
            borderColor: statusColor,
            borderWidth: 1,
            borderRadius: rpx(32),
            paddingHorizontal: rpx(6),
            paddingVertical: rpx(2),
          },
          style,
        ]}
      >
        <Icon name={iconName} color={statusColor} size={iconSize} />
        <Typography.Text
          color={statusColor}
          size={rpx(10)}
          lineHeight={rpx(14)}
        >
          {statusLabel}
        </Typography.Text>
      </StackLayout>
    );
  },
);

export const FaultInspectionItemNote = memo(
  ({
    item,
    style,
    scene,
    withAuthors = true,
    onViewInspectionDetails,
    onViewJobDetails,
  }: {
    item: FaultInspectionItem;
    withAuthors?: boolean;
    scene?: 'task' | 'service-record';
    style?: StyleProp<ViewStyle>;
    onViewInspectionDetails?: (item: FaultInspectionItem) => void;
    onViewJobDetails?: (item: FaultInspectionItem) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout spacing={rpx(0)} style={style}>
        {item.assignedTo != null && withAuthors ? (
          <Li style={{ marginTop: rpx(5) }}>
            <Typography.Text size={rpx(12)} color="#555">
              检修技师: {item.assignedTo.name}
            </Typography.Text>
          </Li>
        ) : null}
        {item.note?.trim() ? (
          <Li style={{ marginTop: rpx(5) }}>
            <Typography.Text size={rpx(12)} color="#555">
              检修备注: {item.note}
            </Typography.Text>
          </Li>
        ) : null}
        <ItemInspectionInfo
          item={item}
          scene={scene}
          withAuthors={withAuthors}
          onViewDetail={onViewInspectionDetails}
          style={{ marginTop: rpx(5) }}
        />
        <ItemConstructionInfo
          item={item}
          scene={scene}
          withAuthors={withAuthors}
          onViewDetail={onViewJobDetails}
          style={{ marginTop: rpx(5) }}
        />
        {item.unsolvedReason?.trim() ? (
          <Li style={{ marginTop: rpx(5) }}>
            <Typography.Text size={rpx(12)} color={StatusColors.Warn}>
              遗留原因: {item.unsolvedReason}
            </Typography.Text>
          </Li>
        ) : null}
        {item.remark?.trim() ? (
          <Li style={{ marginTop: rpx(5) }}>
            <Typography.Text size={rpx(12)} color="#555">
              遗留备注: {item.remark}
            </Typography.Text>
          </Li>
        ) : null}
      </StackLayout>
    );
  },
);

const kMaxItemsToShow = 5;
const kMaxAuthorsToShow = 2;

const ItemInspectionInfo = memo(
  ({
    item,
    scene,
    style,
    withAuthors = true,
    onViewDetail,
  }: {
    item: FaultInspectionItem;
    scene?: 'task' | 'service-record';
    withAuthors?: boolean;
    style?: StyleProp<ViewStyle>;
    onViewDetail?: (item: FaultInspectionItem) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const handleViewDetail = useCallback(() => {
      onViewDetail?.(item);
    }, [item, onViewDetail]);

    if (!item.relatedInspections?.length) {
      if (item.relatedJobs?.length) {
        return null;
      }
      return (
        <Li style={style}>
          <Typography.Text size={rpx(12)} color={theme.colors.status.notice}>
            {scene === 'service-record'
              ? '未进行相关故障检测'
              : '尚未进行相关故障检测'}
          </Typography.Text>
        </Li>
      );
    }

    const inspectedSites = uniq(item.relatedInspections.map(x => x.siteName));
    const authors = uniq(item.relatedInspections.map(x => x.author));
    const faultCount = item.relatedInspections.filter(
      x =>
        x.defectiveLevel === DefectiveLevel.Warning ||
        x.defectiveLevel === DefectiveLevel.Urgent,
    ).length;
    const lastTimestamp = item.relatedInspections
      .map(x => dateFromValue(x.timestamp))
      .sort((a, b) => b.getTime() - a.getTime())[0];

    return (
      <Li style={style}>
        <StackLayout spacing={rpx(4)}>
          <Typography.Text size={rpx(12)} color="#555">
            已对
            {inspectedSites.slice(0, kMaxItemsToShow).map((siteName, i) => (
              <Text key={i}>
                {i > 0 ? '、' : ''}
                <Text
                  style={{
                    color: '#333',
                    textDecorationStyle: 'solid',
                    textDecorationColor: '#333',
                    textDecorationLine: 'underline',
                  }}
                >
                  {siteName}
                </Text>
              </Text>
            ))}
            {inspectedSites.length > kMaxItemsToShow ? '等' : ''}进行检测{', '}
            {faultCount > 0 ? (
              <Text>
                检测出
                <Text style={{ color: theme.colors.status.danger }}>
                  {faultCount}
                </Text>
                处异常
              </Text>
            ) : (
              '未发现明显异常'
            )}
          </Typography.Text>
          {withAuthors && (
            <Typography.Text size={rpx(12)} color="#555">
              {'检测技师: '}
              {authors.slice(0, kMaxAuthorsToShow).map((author, i) => (
                <Text key={i}>
                  {i > 0 ? '、' : ''}
                  <Text
                    style={{
                      color: '#333',
                      textDecorationStyle: 'solid',
                      textDecorationColor: '#333',
                      textDecorationLine: 'underline',
                    }}
                  >
                    {author}
                  </Text>
                </Text>
              ))}
              {authors.length > kMaxAuthorsToShow ? '等, ' : ', '}
              <Text>最后检测时间: {formatDateTime(lastTimestamp)}</Text>
            </Typography.Text>
          )}
          <Typography.Text size={rpx(12)} color="#555" light>
            <Text
              style={{
                color: theme.link,
              }}
              onPress={handleViewDetail}
            >
              查看检测详情 →
            </Text>
          </Typography.Text>
        </StackLayout>
      </Li>
    );
  },
);

const ItemConstructionInfo = memo(
  ({
    item,
    scene,
    withAuthors = true,
    style,
    onViewDetail,
  }: {
    item: FaultInspectionItem;
    scene?: 'task' | 'service-record';
    withAuthors?: boolean;
    style?: StyleProp<ViewStyle>;
    onViewDetail?: (item: FaultInspectionItem) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const finishedJobs = useMemo(
      () =>
        item.relatedJobs?.filter(
          x => x.jobStatus === InspectionTaskJobStatus.Finished,
        ) ?? [],
      [item.relatedJobs],
    );
    const unfinishedJobs = useMemo(
      () =>
        item.relatedJobs?.filter(
          x => x.jobStatus !== InspectionTaskJobStatus.Finished,
        ) ?? [],
      [item.relatedJobs],
    );

    if (!item.relatedJobs?.length) {
      if (!item.relatedInspections?.length) {
        return null;
      }
      return (
        <Li style={style}>
          <Typography.Text size={rpx(12)} color={theme.colors.status.notice}>
            {scene === 'service-record' ? '未进行相关施工' : '尚未进行相关施工'}
          </Typography.Text>
        </Li>
      );
    }

    return (
      <StackLayout style={style} spacing={rpx(5)}>
        {finishedJobs.length > 0 && (
          <Li>
            <RelatedJobsInfo
              label="已完成"
              item={item}
              relatedJobs={finishedJobs}
              scene={scene}
              withAuthors={withAuthors}
              onViewDetail={onViewDetail}
            />
          </Li>
        )}
        {unfinishedJobs.length > 0 && (
          <Li>
            <RelatedJobsInfo
              label="正在进行"
              item={item}
              relatedJobs={unfinishedJobs}
              scene={scene}
              withAuthors={withAuthors}
              onViewDetail={onViewDetail}
            />
          </Li>
        )}
      </StackLayout>
    );
  },
);

const RelatedJobsInfo = memo(
  ({
    label,
    item,
    relatedJobs,
    withAuthors,
    onViewDetail,
  }: {
    label: string;
    relatedJobs: FaultInspectionItemRelatedJob[];
    item: FaultInspectionItem;
    scene?: 'task' | 'service-record';
    withAuthors?: boolean;
    onViewDetail?: (item: FaultInspectionItem) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const jobNames = uniq(relatedJobs.map(x => x.jobName));
    const authors = uniq(relatedJobs.map(x => x.author));
    const lastTimestamp: Date | undefined = relatedJobs
      .map(x => dateFromValue(x.timestamp))
      .sort((a, b) => b.getTime() - a.getTime())[0];

    const handleViewDetail = useCallback(() => {
      onViewDetail?.({
        ...item,
        relatedJobs,
      });
    }, [item, onViewDetail, relatedJobs]);

    return (
      <StackLayout spacing={rpx(4)}>
        <Typography.Text size={rpx(12)} color="#555">
          {label}
          {jobNames.slice(0, kMaxItemsToShow).map((jobName, i) => (
            <Text key={i}>
              {i > 0 ? '、' : ''}
              <Text
                style={{
                  color: theme.colors.status.success,
                  textDecorationStyle: 'solid',
                  textDecorationColor: theme.colors.status.success,
                  textDecorationLine: 'underline',
                }}
              >
                {jobName}
              </Text>
            </Text>
          ))}
          {jobNames.length > kMaxItemsToShow ? '等' : ''}施工反馈
        </Typography.Text>
        {withAuthors && authors.length > 0 && (
          <Typography.Text size={rpx(12)} color="#555">
            施工技师:{' '}
            {authors.slice(0, kMaxAuthorsToShow).map((author, i) => (
              <Text key={i}>
                {i > 0 ? '、' : ''}
                <Text
                  style={{
                    color: '#333',
                    textDecorationStyle: 'solid',
                    textDecorationColor: '#333',
                    textDecorationLine: 'underline',
                  }}
                >
                  {author}
                </Text>
              </Text>
            ))}
            {authors.length > kMaxAuthorsToShow ? '等, ' : ', '}
            <Text>
              最后施工时间:{' '}
              {lastTimestamp ? formatDateTime(lastTimestamp) : '-'}
            </Text>
          </Typography.Text>
        )}
        <Typography.Text size={rpx(12)} color="#555" light>
          <Text style={{ color: theme.link }} onPress={handleViewDetail}>
            查看施工详情 →
          </Text>
        </Typography.Text>
      </StackLayout>
    );
  },
);

const Li = memo(
  ({
    style,
    children,
  }: {
    style?: StyleProp<ViewStyle>;
    children: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        alignItems="flex-start"
        style={style}
        spacing={rpx(0)}
      >
        <Typography.Text
          size={rpx(14)}
          lineHeight={rpx(17)}
          light
          style={{
            top: rpx(2),
          }}
        >
          •
        </Typography.Text>
        <View style={{ flex: 1 }}>{children}</View>
      </StackLayout>
    );
  },
);
